import React from "react";

const S = {
  wrapper: {
    marginTop: '10px', marginLeft: '0px', border: '3px', padding: '40px',
    borderStyle: 'solid', borderColor: 'grey', fontFamily: "'Georgia', serif",
    color: '#1a1a2e', lineHeight: '1.7',
  },
  h1: {
    fontSize: '2rem', fontWeight: '700', borderBottom: '3px solid #2E75B6',
    paddingBottom: '8px', marginBottom: '24px', color: '#1F3864',
  },
  h2: {
    fontSize: '1.3rem', fontWeight: '700', color: '#2E75B6', marginTop: '36px',
    marginBottom: '12px', borderLeft: '4px solid #2E75B6', paddingLeft: '10px',
  },
  p: { marginBottom: '12px', fontSize: '0.97rem' },
  infoBox: {
    background: '#EBF3FB', border: '2px solid #2E75B6', borderRadius: '6px',
    padding: '16px 20px', margin: '16px 0',
  },
  infoBoxTitle: {
    fontWeight: '700', fontFamily: "'Courier New', monospace", fontSize: '0.88rem',
    color: '#1F3864', marginBottom: '8px',
  },
  infoBoxItem: {
    fontSize: '0.9rem', margin: '4px 0', paddingLeft: '12px', borderLeft: '2px solid #2E75B6',
  },
  table: { width: '100%', borderCollapse: 'collapse', marginBottom: '20px', fontSize: '0.92rem' },
  th: { background: '#2E75B6', color: '#fff', padding: '10px 14px', textAlign: 'left', fontWeight: '700', fontSize: '0.9rem' },
  tdE: { padding: '10px 14px', borderBottom: '1px solid #ccd9ea', verticalAlign: 'top', background: '#f5f9ff' },
  tdO: { padding: '10px 14px', borderBottom: '1px solid #ccd9ea', verticalAlign: 'top', background: '#fff' },
  tdL: { fontWeight: '600', color: '#1F3864', whiteSpace: 'nowrap', width: '220px' },
  code: { fontFamily: "'Courier New', monospace", fontSize: '0.88rem', whiteSpace: 'pre-wrap' },
  dWrap: {
    margin: '24px 0', background: '#f8fbff', border: '1px solid #ccd9ea',
    borderRadius: '8px', padding: '20px',
  },
  dTitle: {
    fontWeight: '700', fontSize: '0.95rem', color: '#1F3864',
    marginBottom: '12px', textAlign: 'center', letterSpacing: '0.02em',
  },
  dCaption: { fontSize: '0.84rem', color: '#555', textAlign: 'center', marginTop: '10px', fontStyle: 'italic' },
};

function InfoBox({ title, items }) {
  return (
    <div style={S.infoBox}>
      <div style={S.infoBoxTitle}>{title}</div>
      {items.map((item, i) => <div key={i} style={S.infoBoxItem}>{item}</div>)}
    </div>
  );
}

function Table2({ rows }) {
  return (
    <table style={S.table}>
      <thead><tr>
        <th style={{ ...S.th, width: '220px' }}>Concept</th>
        <th style={S.th}>Formula / Notes</th>
      </tr></thead>
      <tbody>
        {rows.map(([label, content], i) => (
          <tr key={i}>
            <td style={{ ...(i % 2 === 0 ? S.tdE : S.tdO), ...S.tdL }}>{label}</td>
            <td style={{ ...(i % 2 === 0 ? S.tdE : S.tdO), ...S.code }}>{content}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// shared axis/grid helper
function Axes({ ox, oy, W, H, xRange, yRange, sc, label = true }) {
  const toX = x => ox + x * sc;
  const toY = y => oy - y * sc;
  return (
    <g>
      {xRange.map(v => v !== 0 && (
        <g key={`xg${v}`}>
          <line x1={toX(v)} y1={10} x2={toX(v)} y2={H - 10} stroke="#eee" strokeWidth="1" />
          {label && <text x={toX(v)} y={oy + 13} textAnchor="middle" fontSize="9" fill="#bbb">{v}</text>}
        </g>
      ))}
      {yRange.map(v => v !== 0 && (
        <g key={`yg${v}`}>
          <line x1={10} y1={toY(v)} x2={W - 10} y2={toY(v)} stroke="#eee" strokeWidth="1" />
          {label && <text x={ox - 5} y={toY(v) + 3} textAnchor="end" fontSize="9" fill="#bbb">{v}</text>}
        </g>
      ))}
      <line x1={12} y1={oy} x2={W - 10} y2={oy} stroke="#1F3864" strokeWidth="1.5" />
      <line x1={ox} y1={H - 10} x2={ox} y2={10} stroke="#1F3864" strokeWidth="1.5" />
      <text x={W - 8} y={oy - 4} fontSize="11" fill="#1F3864" fontWeight="700">x</text>
      <text x={ox + 4} y={14} fontSize="11" fill="#1F3864" fontWeight="700">y</text>
    </g>
  );
}

// ─────────────────────────────────────────────────────────────────
// DIAGRAM 1 — Coordinate System Anatomy
// ─────────────────────────────────────────────────────────────────
function DiagramCoordinates() {
  const W = 500, H = 280, ox = 220, oy = 150, sc = 55;
  const toX = x => ox + x * sc;
  const toY = y => oy - y * sc;

  const points = [
    { x: 2, y: 2,  label: 'A(2, 2)',   color: '#2E75B6',  q: 'Q I' },
    { x: -2, y: 1.5, label: 'B(-2, 1.5)', color: '#c0392b', q: 'Q II' },
    { x: -1.5, y: -1.5, label: 'C(-1.5,-1.5)', color: '#27ae60', q: 'Q III' },
    { x: 1.5, y: -2, label: 'D(1.5,-2)', color: '#e07b00', q: 'Q IV' },
  ];

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Coordinate System — Quadrants, Axes, and Ordered Pairs (x, y)</div>
      <div style={{ textAlign: 'center' }}>
        <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} style={{ maxWidth: '100%' }}>
          {/* Quadrant shading */}
          <rect x={ox} y={10} width={W - ox - 10} height={oy - 10} fill="#EBF3FB" opacity="0.5" />
          <rect x={12} y={10} width={ox - 12} height={oy - 10} fill="#fff0f0" opacity="0.5" />
          <rect x={12} y={oy} width={ox - 12} height={H - oy - 10} fill="#f0fff0" opacity="0.5" />
          <rect x={ox} y={oy} width={W - ox - 10} height={H - oy - 10} fill="#fffaf0" opacity="0.5" />

          {/* Quadrant labels */}
          {[
            { x: ox + 60, y: oy - 60, label: 'Q I', sub: '(+, +)', color: '#2E75B6' },
            { x: ox - 75, y: oy - 60, label: 'Q II', sub: '(-, +)', color: '#c0392b' },
            { x: ox - 75, y: oy + 70, label: 'Q III', sub: '(-, -)', color: '#27ae60' },
            { x: ox + 60, y: oy + 70, label: 'Q IV', sub: '(+, -)', color: '#e07b00' },
          ].map(({ x, y, label, sub, color }, i) => (
            <g key={i}>
              <text x={x} y={y}     textAnchor="middle" fontSize="13" fill={color} fontWeight="700">{label}</text>
              <text x={x} y={y + 14} textAnchor="middle" fontSize="10" fill={color}>{sub}</text>
            </g>
          ))}

          <Axes ox={ox} oy={oy} W={W} H={H} xRange={[-3,-2,-1,0,1,2,3]} yRange={[-2,-1,0,1,2]} sc={sc} />

          {/* Axis labels */}
          <text x={W - 10} y={oy + 16} fontSize="10" fill="#1F3864" textAnchor="end">x-axis (horizontal)</text>
          <text x={ox + 6} y={14} fontSize="10" fill="#1F3864">y-axis (vertical)</text>
          <text x={ox + 5} y={oy - 5} fontSize="11" fill="#1F3864" fontWeight="700">O (origin)</text>

          {/* Points */}
          {points.map(({ x, y, label, color }, i) => (
            <g key={i}>
              {/* dashed drop lines */}
              <line x1={toX(x)} y1={oy} x2={toX(x)} y2={toY(y)} stroke={color} strokeWidth="1" strokeDasharray="3,3" opacity="0.6" />
              <line x1={ox}     y1={toY(y)} x2={toX(x)} y2={toY(y)} stroke={color} strokeWidth="1" strokeDasharray="3,3" opacity="0.6" />
              <circle cx={toX(x)} cy={toY(y)} r={5} fill={color} stroke="#fff" strokeWidth="1.5" />
              <text x={toX(x) + 7} y={toY(y) - 6} fontSize="11" fill={color} fontWeight="700">{label}</text>
            </g>
          ))}
        </svg>
      </div>
      <div style={S.dCaption}>
        Every point is written as (x, y) — horizontal first, vertical second. The sign of each coordinate tells you the quadrant. Drop lines from a point to each axis show its x and y values.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// DIAGRAM 2 — Slope Visualized
// ─────────────────────────────────────────────────────────────────
function DiagramSlope() {
  const W = 520, H = 220;
  const cases = [
    { label: 'Positive slope', sub: 'm > 0', color: '#2E75B6', p1: [0, 0], p2: [3, 2] },
    { label: 'Negative slope', sub: 'm < 0', color: '#c0392b', p1: [0, 2], p2: [3, 0] },
    { label: 'Zero slope',     sub: 'm = 0', color: '#27ae60', p1: [0, 1], p2: [3, 1] },
    { label: 'Undefined',      sub: 'vertical', color: '#e07b00', p1: [1.5, 0], p2: [1.5, 2.5] },
  ];

  const cW = 120, cH = 180, cPad = { l: 22, r: 10, t: 20, b: 30 };
  const iW = cW - cPad.l - cPad.r, iH = cH - cPad.t - cPad.b;
  const xMax = 3, yMax = 2.5;

  const toX = (x, x0) => x0 + cPad.l + (x / xMax) * iW;
  const toY = (y, y0) => y0 + cPad.t + iH - (y / yMax) * iH;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Slope — Rise Over Run  (m = Δy / Δx)</div>
      <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', flexWrap: 'wrap' }}>
        {cases.map(({ label, sub, color, p1, p2 }, ci) => {
          const x0 = 0, y0 = 0;
          const sx1 = toX(p1[0], x0), sy1 = toY(p1[1], y0);
          const sx2 = toX(p2[0], x0), sy2 = toY(p2[1], y0);
          const isVertical = p1[0] === p2[0];

          // rise/run annotation
          const midX = (sx1 + sx2) / 2, midY = (sy1 + sy2) / 2;
          const rise = p2[1] - p1[1], run = p2[0] - p1[0];

          return (
            <div key={ci} style={{ background: '#fff', border: '1px solid #ccd9ea', borderRadius: '6px', padding: '10px', textAlign: 'center', minWidth: '110px', flex: '1' }}>
              <div style={{ fontWeight: '700', fontSize: '0.82rem', color, marginBottom: '2px' }}>{label}</div>
              <div style={{ fontSize: '0.74rem', color: '#666', marginBottom: '4px', fontFamily: "'Courier New',monospace" }}>{sub}</div>
              <svg width={cW} height={cH} viewBox={`0 0 ${cW} ${cH}`} style={{ maxWidth: '100%' }}>
                {/* Axes */}
                <line x1={cPad.l} y1={cPad.t} x2={cPad.l} y2={cPad.t+iH} stroke="#ddd" strokeWidth="1"/>
                <line x1={cPad.l} y1={cPad.t+iH} x2={cPad.l+iW} y2={cPad.t+iH} stroke="#ddd" strokeWidth="1"/>
                {/* Line */}
                <line x1={sx1} y1={sy1} x2={sx2} y2={sy2} stroke={color} strokeWidth="2.5" strokeLinecap="round"/>
                {/* Points */}
                <circle cx={sx1} cy={sy1} r={4} fill={color} stroke="#fff" strokeWidth="1.2"/>
                <circle cx={sx2} cy={sy2} r={4} fill={color} stroke="#fff" strokeWidth="1.2"/>
                {/* Rise/run triangle for non-vertical, non-zero */}
                {!isVertical && rise !== 0 && (
                  <g>
                    <line x1={sx1} y1={sy1} x2={sx2} y2={sy1} stroke="#aaa" strokeWidth="1" strokeDasharray="3,2"/>
                    <line x1={sx2} y1={sy1} x2={sx2} y2={sy2} stroke="#aaa" strokeWidth="1" strokeDasharray="3,2"/>
                    <text x={(sx1+sx2)/2} y={sy1+11} textAnchor="middle" fontSize="9" fill="#888">run={run}</text>
                    <text x={sx2+5} y={(sy1+sy2)/2+3} textAnchor="start" fontSize="9" fill="#888">rise={rise > 0 ? '+' : ''}{rise}</text>
                  </g>
                )}
                {/* Slope formula */}
                {!isVertical && (
                  <text x={cPad.l+iW/2} y={cPad.t+iH+20} textAnchor="middle" fontSize="10" fill={color} fontWeight="700" fontFamily="'Courier New',monospace">
                    {rise === 0 ? 'm = 0' : `m = ${rise}/${run}`}
                  </text>
                )}
                {isVertical && (
                  <text x={cPad.l+iW/2} y={cPad.t+iH+20} textAnchor="middle" fontSize="10" fill={color} fontWeight="700" fontFamily="'Courier New',monospace">
                    m = undef
                  </text>
                )}
              </svg>
            </div>
          );
        })}
      </div>
      <div style={S.dCaption}>
        Slope = rise / run = (y₂ - y₁) / (x₂ - x₁). Positive slopes rise left to right. Negative slopes fall. Horizontal lines have m = 0. Vertical lines have undefined slope (division by zero).
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// DIAGRAM 3 — Function Transformations
// ─────────────────────────────────────────────────────────────────
function DiagramTransformations() {
  const W = 540, H = 260;
  const ox = 100, oy = 150, sc = 50;
  const toX = x => ox + x * sc;
  const toY = y => oy - y * sc;

  const base = x => x * x;              // f(x) = x²
  const cases = [
    { label: 'f(x) = x²',        fn: x => x*x,       color: '#1F3864', dash: '' },
    { label: 'f(x) + 2',         fn: x => x*x + 2,   color: '#2E75B6', dash: '',   note: 'shift up 2' },
    { label: 'f(x+2)',            fn: x => (x+2)**2,  color: '#c0392b', dash: '',   note: 'shift left 2' },
    { label: '2·f(x)',            fn: x => 2*x*x,     color: '#27ae60', dash: '',   note: 'stretch ×2' },
    { label: '-f(x)',             fn: x => -x*x,      color: '#e07b00', dash: '5,3',note: 'reflect y-axis' },
  ];

  const makePath = fn => {
    const pts = [];
    for (let xi = -3; xi <= 3; xi += 0.06) {
      const y = fn(xi);
      const [sx, sy] = [toX(xi), toY(y)];
      if (sy > 5 && sy < H - 5) pts.push(`${sx},${sy}`);
    }
    return pts.length > 1 ? 'M ' + pts.join(' L ') : '';
  };

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Function Transformations — How Adding, Multiplying, and Negating Shift the Graph</div>
      <div style={{ textAlign: 'center' }}>
        <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} style={{ maxWidth: '100%' }}>
          <Axes ox={ox} oy={oy} W={W} H={H} xRange={[-3,-2,-1,0,1,2,3]} yRange={[-2,-1,0,1,2,3,4]} sc={sc} />

          {/* Curves — base first (thickest), then others */}
          {cases.map(({ fn, color, dash }, i) => (
            <path key={i} d={makePath(fn)} fill="none" stroke={color}
              strokeWidth={i === 0 ? 3 : 1.8} strokeDasharray={dash} strokeLinejoin="round" opacity={i === 0 ? 1 : 0.85}/>
          ))}

          {/* Legend */}
          <rect x={W - 175} y={12} width={168} height={cases.length * 22 + 10} rx={5} fill="#fff" stroke="#ccd9ea"/>
          {cases.map(({ label, note, color, dash }, i) => (
            <g key={i}>
              <line x1={W-168} y1={25+i*22} x2={W-148} y2={25+i*22} stroke={color} strokeWidth={i===0?3:1.8} strokeDasharray={dash}/>
              <text x={W-144} y={29+i*22} fontSize="10" fill={color} fontWeight={i===0?'700':'400'} fontFamily="'Courier New',monospace">{label}</text>
              {note && <text x={W-144} y={29+i*22+11} fontSize="9" fill="#888" fontStyle="italic">{note}</text>}
            </g>
          ))}

          {/* Vertex labels */}
          <text x={toX(0)+5} y={toY(0)-8} fontSize="10" fill="#1F3864" fontWeight="700">(0,0)</text>
          <text x={toX(0)+5} y={toY(2)-8} fontSize="10" fill="#2E75B6">(0,2)</text>
          <text x={toX(-2)+5} y={toY(0)-8} fontSize="10" fill="#c0392b">(-2,0)</text>
        </svg>
      </div>
      <div style={S.dCaption}>
        The base curve f(x) = x² (dark) transformed four ways. Adding to f(x) shifts vertically. Adding to x shifts horizontally (opposite direction). Multiplying by a scales. Negating reflects.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// DIAGRAM 4 — Conic Sections
// ─────────────────────────────────────────────────────────────────
function DiagramConics() {
  const W = 540, H = 260;
  const cardW = 120, cardH = 220;
  const positions = [20, 155, 290, 415];

  const conics = [
    {
      label: 'Circle',
      eq: 'x²/r² + y²/r² = 1',
      sub: 'r = 2',
      color: '#2E75B6',
      draw: (ox, oy, sc) => `M ${ox+2*sc} ${oy} A ${2*sc} ${2*sc} 0 1 0 ${ox-2*sc} ${oy} A ${2*sc} ${2*sc} 0 1 0 ${ox+2*sc} ${oy}`,
    },
    {
      label: 'Ellipse',
      eq: 'x²/a² + y²/b² = 1',
      sub: 'a=2.5, b=1.5',
      color: '#c0392b',
      draw: (ox, oy, sc) => {
        const a = 2.5*sc, b = 1.5*sc;
        return `M ${ox+a} ${oy} A ${a} ${b} 0 1 0 ${ox-a} ${oy} A ${a} ${b} 0 1 0 ${ox+a} ${oy}`;
      },
    },
    {
      label: 'Parabola',
      eq: 'y = ax² + bx + c',
      sub: 'y = x²',
      color: '#27ae60',
      draw: (ox, oy, sc) => {
        const pts = [];
        for (let xi = -2.2; xi <= 2.2; xi += 0.08) {
          const sx = ox + xi*sc, sy = oy - xi*xi*sc*0.7;
          if (sy > 10 && sy < cardH - 10) pts.push(`${sx},${sy}`);
        }
        return 'M ' + pts.join(' L ');
      },
    },
    {
      label: 'Hyperbola',
      eq: 'x²/a² - y²/b² = 1',
      sub: 'a=1.2, b=1',
      color: '#e07b00',
      draw: (ox, oy, sc) => {
        const a = 1.2, b = 1.0;
        const pts1 = [], pts2 = [];
        for (let y = -2.5; y <= 2.5; y += 0.08) {
          const x = a * Math.sqrt(1 + (y*y)/(b*b));
          const sx1 = ox + x*sc, sx2 = ox - x*sc, sy = oy - y*sc;
          if (sy > 10 && sy < cardH-10) {
            pts1.push(`${sx1},${sy}`);
            pts2.push(`${sx2},${sy}`);
          }
        }
        return ('M ' + pts1.join(' L ')) + ' ' + ('M ' + pts2.join(' L '));
      },
    },
  ];

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Conic Sections — Circle, Ellipse, Parabola, Hyperbola</div>
      <div style={{ textAlign: 'center' }}>
        <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} style={{ maxWidth: '100%' }}>
          {conics.map(({ label, eq, sub, color, draw }, ci) => {
            const x0 = positions[ci], cSc = 22;
            const ox = x0 + cardW/2, oy = 130;

            // mini axes
            const axLen = 50;
            return (
              <g key={ci}>
                <rect x={x0} y={10} width={cardW} height={cardH-10} rx={5} fill="#fff" stroke="#ccd9ea"/>
                <text x={ox} y={28} textAnchor="middle" fontSize="12" fill={color} fontWeight="700">{label}</text>
                <text x={ox} y={42} textAnchor="middle" fontSize="9" fill="#888" fontFamily="'Courier New',monospace">{eq}</text>
                <text x={ox} y={54} textAnchor="middle" fontSize="9" fill="#aaa">{sub}</text>
                {/* Mini axes */}
                <line x1={ox-axLen} y1={oy} x2={ox+axLen} y2={oy} stroke="#eee" strokeWidth="1"/>
                <line x1={ox} y1={oy-axLen} x2={ox} y2={oy+axLen} stroke="#eee" strokeWidth="1"/>
                {/* Curve */}
                <path d={draw(ox, oy, cSc)} fill="none" stroke={color} strokeWidth="2.2" strokeLinejoin="round"/>
                {/* Center dot */}
                <circle cx={ox} cy={oy} r={2.5} fill="#aaa"/>
              </g>
            );
          })}

          {/* ME note */}
          <rect x={10} y={H-38} width={W-20} height={30} rx={4} fill="#EBF3FB" stroke="#2E75B6" strokeWidth="1"/>
          <text x={W/2} y={H-23} textAnchor="middle" fontSize="10" fill="#1F3864" fontWeight="700">
            ME uses: Circular cross-sections (shafts) · Elliptical pressure vessels · Parabolic reflectors · Hyperbolic gears
          </text>
          <text x={W/2} y={H-10} textAnchor="middle" fontSize="9" fill="#555">
            All are cross-sections of a cone at different angles — which is why they share the same general equation
          </text>
        </svg>
      </div>
      <div style={S.dCaption}>
        All four conics come from slicing a cone at different angles. Their equations share the form Ax² + Bxy + Cy² + Dx + Ey + F = 0. The discriminant B² - 4AC tells you which type.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// DIAGRAM 5 — Function Intersections
// ─────────────────────────────────────────────────────────────────
function DiagramIntersections() {
  const W = 500, H = 260, ox = 180, oy = 150, sc = 55;
  const toX = x => ox + x * sc;
  const toY = y => oy - y * sc;

  // f(x) = x² - 1  and  g(x) = x + 1  — intersect at x = -1 (y=0) and x = 2 (y=3)
  const fPts = [], gPts = [];
  for (let xi = -2.5; xi <= 2.8; xi += 0.04) {
    const yf = xi * xi - 1;
    const yg = xi + 1;
    const [sx, sy] = [toX(xi), toY(yf)];
    if (sy > 10 && sy < H-10) fPts.push(`${sx},${sy}`);
    const [sx2, sy2] = [toX(xi), toY(yg)];
    if (sy2 > 10 && sy2 < H-10) gPts.push(`${sx2},${sy2}`);
  }

  const intersections = [{ x: -1, y: 0 }, { x: 2, y: 3 }];

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Function Intersections — Graphical Solutions to Systems of Equations</div>
      <div style={{ textAlign: 'center' }}>
        <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} style={{ maxWidth: '100%' }}>
          <Axes ox={ox} oy={oy} W={W} H={H} xRange={[-2,-1,0,1,2]} yRange={[-1,0,1,2,3]} sc={sc} />

          {/* Curves */}
          <path d={'M ' + fPts.join(' L ')} fill="none" stroke="#2E75B6" strokeWidth="2.3" strokeLinejoin="round"/>
          <path d={'M ' + gPts.join(' L ')} fill="none" stroke="#c0392b" strokeWidth="2.3" strokeLinejoin="round"/>

          {/* Labels */}
          {(() => {
            const [lx, ly] = [toX(1.8), toY(1.8*1.8-1)];
            const [rx, ry] = [toX(-2), toY(-1)];
            return <>
              <text x={lx+6} y={ly-6} fontSize="12" fill="#2E75B6" fontWeight="700">f(x) = x² - 1</text>
              <text x={rx-4} y={ry-6} fontSize="12" fill="#c0392b" fontWeight="700" textAnchor="end">g(x) = x + 1</text>
            </>;
          })()}

          {/* Intersection markers */}
          {intersections.map(({ x, y }, i) => {
            const [sx, sy] = [toX(x), toY(y)];
            return (
              <g key={i}>
                {/* Pulse rings */}
                <circle cx={sx} cy={sy} r={12} fill="none" stroke="#e07b00" strokeWidth="1" opacity="0.4"/>
                <circle cx={sx} cy={sy} r={7}  fill="#e07b00" stroke="#fff" strokeWidth="1.5"/>
                {/* Drop lines */}
                <line x1={sx} y1={oy} x2={sx} y2={sy} stroke="#e07b00" strokeWidth="1" strokeDasharray="3,3"/>
                <line x1={ox} y1={sy} x2={sx} y2={sy} stroke="#e07b00" strokeWidth="1" strokeDasharray="3,3"/>
                {/* Label */}
                <rect x={sx+10} y={sy-20} width={70} height={32} rx={3} fill="#fff7ee" stroke="#e07b00" strokeWidth="1"/>
                <text x={sx+15} y={sy-7} fontSize="10" fill="#e07b00" fontWeight="700">({x}, {y})</text>
                <text x={sx+15} y={sy+7} fontSize="9" fill="#888">solution {i+1}</text>
              </g>
            );
          })}

          {/* System box */}
          <rect x={10} y={14} width={145} height={55} rx={4} fill="#fff" stroke="#ccd9ea"/>
          <text x={82} y={30} textAnchor="middle" fontSize="11" fill="#1F3864" fontWeight="700">System of equations:</text>
          <text x={18} y={46} fontSize="11" fill="#2E75B6" fontFamily="'Courier New',monospace">y = x² - 1</text>
          <text x={18} y={61} fontSize="11" fill="#c0392b" fontFamily="'Courier New',monospace">y = x + 1</text>
        </svg>
      </div>
      <div style={S.dCaption}>
        Intersection points are the x values where both functions are equal — i.e. the solutions to f(x) = g(x). Here x²-1 = x+1 gives x = -1 and x = 2. Graphing makes it easy to see how many solutions exist before solving algebraically.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// DIAGRAM 6 — Polar Coordinates
// ─────────────────────────────────────────────────────────────────
function DiagramPolar() {
  const W = 520, H = 280, cx = 180, cy = 150, maxR = 120;
  const toSVG = (r, theta) => [cx + r * Math.cos(theta), cy - r * Math.sin(theta)];
  const deg = d => d * Math.PI / 180;

  // Concentric circles
  const rings = [30, 60, 90, 120];
  // Radial lines every 30 degrees
  const radials = [0, 30, 60, 90, 120, 150, 180, 210, 240, 270, 300, 330];

  // Rose curve r = 2*cos(3θ) — 3-petal
  const rosePts = [];
  for (let t = 0; t <= Math.PI; t += 0.02) {
    const r = maxR * 0.7 * Math.abs(Math.cos(3 * t));
    const [sx, sy] = toSVG(r, t);
    if (sx > 10 && sx < W - 200 && sy > 10 && sy < H - 10) rosePts.push(`${sx},${sy}`);
  }

  // Sample point
  const sampleR = 80, sampleTheta = deg(50);
  const [spx, spy] = toSVG(sampleR, sampleTheta);

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Polar Coordinates — (r, θ) and the Rose Curve r = cos(3θ)</div>
      <div style={{ textAlign: 'center' }}>
        <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} style={{ maxWidth: '100%' }}>
          {/* Rings */}
          {rings.map(r => <circle key={r} cx={cx} cy={cy} r={r} fill="none" stroke="#e0e8f0" strokeWidth="1"/>)}
          {/* r labels */}
          {rings.map(r => <text key={r} x={cx + r + 3} y={cy + 10} fontSize="9" fill="#bbb">{(r/30).toFixed(0)}</text>)}

          {/* Radial lines */}
          {radials.map(d => {
            const [sx, sy] = toSVG(maxR, deg(d));
            return <line key={d} x1={cx} y1={cy} x2={sx} y2={sy} stroke="#e0e8f0" strokeWidth="1"/>;
          })}

          {/* Angle labels */}
          {[0, 30, 60, 90, 120, 150, 180, 210, 240, 270, 300, 330].map(d => {
            const [sx, sy] = toSVG(maxR + 14, deg(d));
            return <text key={d} x={sx} y={sy + 4} textAnchor="middle" fontSize="9" fill="#aaa">{d}°</text>;
          })}

          {/* Axes */}
          <line x1={cx - maxR - 14} y1={cy} x2={cx + maxR + 14} y2={cy} stroke="#1F3864" strokeWidth="1.3"/>
          <line x1={cx} y1={cy + maxR + 14} x2={cx} y2={cy - maxR - 14} stroke="#1F3864" strokeWidth="1.3"/>
          <text x={cx + maxR + 16} y={cy + 4} fontSize="10" fill="#1F3864" fontWeight="700">0°</text>
          <text x={cx + 4} y={cy - maxR - 16} fontSize="10" fill="#1F3864">90°</text>

          {/* Rose curve */}
          {[0, 1, 2].map(k => {
            const pts = [];
            for (let t = k*Math.PI/3 - Math.PI/6; t <= k*Math.PI/3 + Math.PI/6 + 0.02; t += 0.015) {
              const r = maxR * 0.72 * Math.cos(3 * t);
              if (r >= 0) {
                const [sx, sy] = toSVG(r, t);
                pts.push(`${sx},${sy}`);
              }
            }
            return pts.length > 1 ? <path key={k} d={'M ' + pts.join(' L ')} fill="#2E75B6" fillOpacity="0.15" stroke="#2E75B6" strokeWidth="2"/> : null;
          })}

          {/* Origin */}
          <circle cx={cx} cy={cy} r={3} fill="#1F3864"/>
          <text x={cx+5} y={cy-5} fontSize="9" fill="#1F3864" fontWeight="700">O</text>

          {/* Sample point annotation */}
          <line x1={cx} y1={cy} x2={spx} y2={spy} stroke="#c0392b" strokeWidth="2"/>
          <circle cx={spx} cy={spy} r={5} fill="#c0392b" stroke="#fff" strokeWidth="1.5"/>
          {/* Arc for angle */}
          <path d={`M ${cx+25},${cy} A 25 25 0 0 0 ${cx+25*Math.cos(sampleTheta)},${cy-25*Math.sin(sampleTheta)}`} fill="none" stroke="#e07b00" strokeWidth="1.5"/>
          <text x={cx+30} y={cy-18} fontSize="10" fill="#e07b00" fontWeight="700">θ = 50°</text>
          <text x={spx+7} y={spy-7} fontSize="10" fill="#c0392b" fontWeight="700">P(r, θ)</text>
          <text x={cx + (spx-cx)*0.45 + 5} y={cy + (spy-cy)*0.45 - 6} fontSize="9" fill="#c0392b">r</text>

          {/* Info panel */}
          <rect x={310} y={20} width={200} height={220} rx={5} fill="#fff" stroke="#ccd9ea"/>
          <text x={410} y={38} textAnchor="middle" fontSize="11" fill="#1F3864" fontWeight="700">Polar vs Cartesian</text>
          {[
            ['x = r·cos(θ)', '#2E75B6'],
            ['y = r·sin(θ)', '#2E75B6'],
            ['r = sqrt(x²+y²)', '#c0392b'],
            ['θ = arctan(y/x)', '#c0392b'],
          ].map(([txt, col], i) => (
            <text key={i} x={320} y={58+i*18} fontSize="11" fill={col} fontFamily="'Courier New',monospace">{txt}</text>
          ))}
          <line x1={320} y1={135} x2={500} y2={135} stroke="#eee" strokeWidth="1"/>
          <text x={410} y={152} textAnchor="middle" fontSize="11" fill="#1F3864" fontWeight="700">Common Polar Curves</text>
          {[
            ['Circle:   r = a', '#2E75B6'],
            ['Rose:     r = cos(nθ)', '#27ae60'],
            ['Spiral:   r = aθ', '#c0392b'],
            ['Cardioid: r = a(1+cosθ)', '#e07b00'],
          ].map(([txt, col], i) => (
            <text key={i} x={320} y={168+i*18} fontSize="10" fill={col} fontFamily="'Courier New',monospace">{txt}</text>
          ))}
        </svg>
      </div>
      <div style={S.dCaption}>
        Polar coordinates describe position by distance r from the origin and angle θ from the positive x-axis. Essential for rotating machinery, antenna patterns, fluid vortices, and any system with circular symmetry.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────
export default function GraphicEquations(props) {
  return (
    <div style={S.wrapper}>
      <h1 style={S.h1}>Graphic Equations</h1>

      <p style={S.p}>
        Graphing translates abstract equations into visual shapes you can reason about intuitively. In mechanical engineering you will read and produce graphs constantly — from load vs. deflection curves to frequency response plots to CAD cross-sections. Understanding the visual language of math makes every other topic easier.
      </p>

      <InfoBox
        title="Key Idea: Every equation is a picture. Every picture has an equation."
        items={[
          "A graph is the set of all (x, y) points that satisfy an equation.",
          "The shape of a graph reveals the behavior of the function — growth, decay, oscillation, limits.",
          "Intersections of graphs are solutions to systems of equations.",
          "Transformations (shifts, stretches, reflections) follow consistent, predictable rules.",
        ]}
      />

      {/* Coordinate System */}
      <h2 style={S.h2}>The Coordinate System</h2>
      <DiagramCoordinates />

      <Table2 rows={[
        ["Ordered pair",        "(x, y) — x is horizontal position, y is vertical position"],
        ["Origin",              "(0, 0) — where the axes cross"],
        ["Quadrant I",          "x > 0, y > 0  — upper right"],
        ["Quadrant II",         "x < 0, y > 0  — upper left"],
        ["Quadrant III",        "x < 0, y < 0  — lower left"],
        ["Quadrant IV",         "x > 0, y < 0  — lower right"],
        ["Distance formula",    "d = sqrt((x₂-x₁)² + (y₂-y₁)²)  — comes directly from Pythagorean theorem"],
        ["Midpoint formula",    "M = ((x₁+x₂)/2,  (y₁+y₂)/2)"],
      ]} />

      {/* Slope */}
      <h2 style={S.h2}>Slope</h2>
      <DiagramSlope />

      <Table2 rows={[
        ["Slope definition",    "m = rise/run = (y₂ - y₁) / (x₂ - x₁) = Δy / Δx"],
        ["Positive slope",      "m > 0 — line rises left to right"],
        ["Negative slope",      "m < 0 — line falls left to right"],
        ["Zero slope",          "m = 0 — horizontal line  (y = constant)"],
        ["Undefined slope",     "vertical line  x = constant  (Δx = 0, division by zero)"],
        ["Parallel lines",      "Same slope: m₁ = m₂"],
        ["Perpendicular lines", "Negative reciprocal slopes: m₁ · m₂ = -1"],
        ["Slope in engineering","Rate of change on any graph — stress/strain slope = elastic modulus E"],
      ]} />

      {/* Transformations */}
      <h2 style={S.h2}>Function Transformations</h2>
      <DiagramTransformations />

      <Table2 rows={[
        ["Vertical shift",      "y = f(x) + k — shifts the graph up by k (down if k < 0)"],
        ["Horizontal shift",    "y = f(x + h) — shifts left by h  (right if h < 0)\nNote: the direction is opposite to the sign"],
        ["Vertical stretch",    "y = a·f(x) — multiplies all y-values by a\na > 1: stretch.  0 < a < 1: compress.  a < 0: reflect over x-axis"],
        ["Horizontal stretch",  "y = f(bx) — compresses horizontally by factor b"],
        ["Reflection x-axis",   "y = -f(x) — flips graph upside down"],
        ["Reflection y-axis",   "y = f(-x) — flips graph left to right"],
        ["Combined form",       "y = a·f(b(x - h)) + k\nApply in order: stretch/compress → reflect → shift"],
      ]} />

      {/* Conic Sections */}
      <h2 style={S.h2}>Conic Sections</h2>
      <DiagramConics />

      <Table2 rows={[
        ["General conic form",  "Ax² + Bxy + Cy² + Dx + Ey + F = 0\nDiscriminant B² - 4AC determines type"],
        ["Circle",              "x² + y² = r²  (center at origin)\n(x-h)² + (y-k)² = r²  (center at h,k)\nB²-4AC < 0, A = C"],
        ["Ellipse",             "x²/a² + y²/b² = 1\na = semi-major axis, b = semi-minor axis\nB²-4AC < 0, A ≠ C"],
        ["Parabola",            "y = ax² + bx + c  or  x = ay² + by + c\nOne squared term only.  B²-4AC = 0"],
        ["Hyperbola",           "x²/a² - y²/b² = 1  (horizontal)\ny²/b² - x²/a² = 1  (vertical)\nB²-4AC > 0"],
        ["ME applications",     "Circles: shafts, holes, pressure vessels\nEllipses: cams, stress ellipses\nParabolas: reflectors, projectile paths\nHyperbolas: gear profiles, cooling towers"],
      ]} />

      {/* Intersections */}
      <h2 style={S.h2}>Function Intersections and Systems</h2>
      <DiagramIntersections />

      <Table2 rows={[
        ["Intersection = solution", "Where two graphs cross, both equations are satisfied simultaneously"],
        ["Number of solutions",     "0 intersections → no solution\n1 intersection → one solution (tangent)\n2+ intersections → multiple solutions"],
        ["Solving graphically",     "Plot both functions and read off the x-values where they cross\nUseful for estimating before algebraic solution"],
        ["Solving algebraically",   "Set f(x) = g(x) and solve for x\nThen substitute back to find y"],
        ["ME use",                  "Finding operating point where supply = demand\nFinding resonance: excitation frequency = natural frequency\nFinding when two component sizes are equal"],
      ]} />

      {/* Polar Coordinates */}
      <h2 style={S.h2}>Polar Coordinates</h2>
      <DiagramPolar />

      <Table2 rows={[
        ["Polar notation",          "(r, θ) — r is distance from origin, θ is angle from positive x-axis"],
        ["Cartesian to polar",      "r = sqrt(x² + y²)\nθ = arctan(y/x)  — adjust quadrant as needed"],
        ["Polar to Cartesian",      "x = r·cos(θ)\ny = r·sin(θ)"],
        ["Circle at origin",        "r = a  — a circle of radius a centered at origin"],
        ["Rose curves",             "r = a·cos(nθ) or r = a·sin(nθ)\nn petals if n is odd, 2n petals if n is even"],
        ["Archimedean spiral",      "r = aθ — radius grows linearly with angle\nUsed in scroll compressors, watch springs"],
        ["ME applications",         "Rotating machinery: position in polar is natural\nAntenna/radiation patterns plotted in polar\nFluid vortex: streamlines are circles in polar\nCam profiles: r(θ) defines the cam shape directly"],
      ]} />

      <InfoBox
        title="Connecting Graphs to the Rest of Your Math"
        items={[
          "Derivatives (Calculus): slope of the tangent line at a point = the derivative at that point.",
          "Integrals (Calculus): area under a curve = the definite integral between those x values.",
          "Eigenvalues (Matrices): graphically, an eigenvalue is where det([A]-λ[I]) = 0 — the roots of a plotted polynomial.",
          "Characteristic equations (ODEs): roots of the characteristic polynomial determine whether the graph oscillates, decays, or grows.",
          "Polar + trig: any rotating system is better described in polar form — r(θ) instead of y(x).",
        ]}
      />
    </div>
  );
}